import { Card, CardContent } from "@/components/ui/card";

/* Generic small UI helpers used across the analytics tabs and presentation
   slides. Pure presentational, no state. */

export function FilterField({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}

export function NativeSelect({ value, onChange, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white"
      data-testid={testid}
    >
      <option value="">Todos</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

const TONES = {
  blue: "text-blue-700 bg-blue-50",
  green: "text-emerald-700 bg-emerald-50",
  amber: "text-amber-700 bg-amber-50",
  red: "text-red-600 bg-red-50",
  slate: "text-slate-700 bg-slate-100",
};

export function Kpi({ icon: Icon, tone, label, value, testid }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-3">
        <div className={`h-8 w-8 rounded-md flex items-center justify-center mb-2 ${TONES[tone]}`}>
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium truncate">
          {label}
        </div>
        <div
          className="text-lg font-display font-bold text-slate-900 mt-0.5 truncate"
          data-testid={testid}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

export function ChartCard({ title, children, className = "" }) {
  return (
    <Card className={`border-slate-200 shadow-sm ${className}`}>
      <CardContent className="p-5">
        <h2 className="font-display text-base font-semibold text-slate-900 mb-3">
          {title}
        </h2>
        {children}
      </CardContent>
    </Card>
  );
}

export function Empty({ text = "Sem dados no período filtrado." }) {
  return <div className="text-sm text-slate-400 text-center py-12">{text}</div>;
}

export function RankingCard({ icon: Icon, title, rows, subtitle }) {
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-5">
        <h2 className="font-display text-base font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <Icon className="h-4 w-4 text-blue-700" /> {title}
        </h2>
        {subtitle && <p className="text-[11px] text-slate-500 mb-3">{subtitle}</p>}
        {rows.length === 0 ? <Empty /> : (
          <div className="space-y-2">
            {rows.map((r, i) => {
              const pct = Math.round((r.total / max) * 100);
              return (
                <div key={r.id || i} className="flex items-center gap-3">
                  <div className="w-44 text-sm text-slate-700 truncate" title={r.name}>{r.name}</div>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-blue-700" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-10 text-right text-sm font-semibold text-slate-900">{r.total}</div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function Heatmap({ hm }) {
  if (!hm || !hm.rows || hm.rows.length === 0) return <Empty />;
  const max = Math.max(1, ...hm.rows.flatMap((r) => r.cells.map((c) => c.count)));
  const cellColor = (c) => {
    if (c === 0) return "bg-slate-50 text-slate-300";
    const intensity = Math.min(1, c / max);
    if (intensity > 0.75) return "bg-blue-700 text-white";
    if (intensity > 0.5) return "bg-blue-500 text-white";
    if (intensity > 0.25) return "bg-blue-300 text-blue-900";
    return "bg-blue-100 text-blue-900";
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className="text-left px-2 py-1.5 sticky left-0 bg-white"></th>
            {hm.procedures.map((p) => (
              <th key={p.id} className="p-1.5 font-normal text-slate-600 text-[10px]" title={p.name}>
                <div className="transform -rotate-45 origin-bottom-left whitespace-nowrap h-16">
                  {p.name.slice(0, 18)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hm.rows.map((r) => (
            <tr key={r.surgeon_id}>
              <td className="text-left px-2 py-1.5 font-medium text-slate-700 sticky left-0 bg-white whitespace-nowrap">
                {r.surgeon}
              </td>
              {r.cells.map((c) => (
                <td key={c.procedure_id} className={`text-center font-semibold ${cellColor(c.count)} p-0`}>
                  <div className="h-10 w-16 flex items-center justify-center">{c.count || ""}</div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-slate-400 mt-2">
        Intensidade da cor = volume de casos. Cirurgiões ordenados por total; procedimentos idem.
      </div>
    </div>
  );
}

export function InsightBody({ text }) {
  if (!text) return null;
  const blocks = text.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  function renderInline(s) {
    const parts = [];
    const re = /\*\*([^*]+)\*\*/g;
    let last = 0, m, idx = 0;
    while ((m = re.exec(s)) !== null) {
      if (m.index > last) parts.push(s.slice(last, m.index));
      parts.push(
        <strong key={`b-${idx++}-${m.index}`} className="font-semibold">{m[1]}</strong>
      );
      last = m.index + m[0].length;
    }
    if (last < s.length) parts.push(s.slice(last));
    return parts;
  }
  return (
    <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
      {blocks.map((b, i) => {
        const blockKey = `${i}-${b.slice(0, 24)}`;
        if (b.startsWith("## ")) {
          return (
            <h3 key={blockKey} className="font-display text-sm font-bold text-slate-900 uppercase tracking-wider mt-4 first:mt-0">
              {b.slice(3)}
            </h3>
          );
        }
        if (/^\d+\.\s/.test(b)) {
          const items = b.split(/\n(?=\d+\.\s)/).map((it) => it.replace(/^\d+\.\s/, ""));
          return (
            <ol key={blockKey} className="list-decimal ml-5 space-y-1.5">
              {items.map((it, j) => (
                <li key={`${blockKey}-li-${j}`}>{renderInline(it)}</li>
              ))}
            </ol>
          );
        }
        return <p key={blockKey}>{renderInline(b)}</p>;
      })}
    </div>
  );
}
